export interface NodePoint {
  x: number;
  y: number;
  z: number;
}

/**
 * Parse a coordinate input string into a numeric array.
 * Accept separators: comma, semicolon, or whitespace. Ignore empty tokens and non-numeric entries.
 */
export function parseCoordinateInput(input: string): number[] {
  if (!input) return [];
  // split on comma, semicolon or any whitespace
  const tokens = input.split(/[,;\s]+/).map(t => t.trim()).filter(t => t.length > 0);
  const nums: number[] = [];
  for (const t of tokens) {
    const n = Number(t);
    if (!Number.isNaN(n) && isFinite(n)) nums.push(n);
  }
  return nums;
}

/**
 * Generate points by zipping arrays positionally. Rules:
 * - parse inputs using parseCoordinateInput
 * - determine max length between x,y,z arrays (at least 1)
 * - for arrays with length 0 -> fill with zeros
 * - for arrays with length 1 -> repeat that value for all indices
 * - for arrays with length >1 and < maxLen -> pad with 0s
 * - produce points by taking i-th value from each array for i in [0..maxLen-1]
 */
export function generateNodePoints(xCsv: string, yCsv: string, zCsv: string): NodePoint[] {
  let xs = parseCoordinateInput(xCsv);
  let ys = parseCoordinateInput(yCsv);
  let zs = parseCoordinateInput(zCsv);

  // Default empty inputs to [0] so we always have a value per axis
  if (xs.length === 0) xs = [0];
  if (ys.length === 0) ys = [0];
  if (zs.length === 0) zs = [0];

  const lx = xs.length, ly = ys.length, lz = zs.length;

  // Case 1: All arrays have the same length -> positional pairing
  if (lx === ly && ly === lz) {
    const points: NodePoint[] = [];
    for (let i = 0; i < lx; i++) {
      points.push({ x: xs[i], y: ys[i], z: zs[i] });
    }
    return points;
  }

  // Case 2: Otherwise, generate Cartesian product across axes
  // Axes with length 1 behave as constants; axes with length > 1 vary.
  const points: NodePoint[] = [];
  for (let i = 0; i < lx; i++) {
    const xVal = xs[i];
    for (let j = 0; j < ly; j++) {
      const yVal = ys[j];
      for (let k = 0; k < lz; k++) {
        const zVal = zs[k];
        points.push({ x: xVal, y: yVal, z: zVal });
      }
    }
  }
  return points;
}

export default generateNodePoints;

/**
 * Generate offset nodes from a base point.
 * - base: starting NodePoint
 * - count: total nodes to produce (>=1)
 * - offset: per-step offset to apply to previous node
 * Behavior: returns array of length `count` where first element is `base` and
 * subsequent elements are previous + offset. If offset is (0,0,0) this returns
 * only the base (avoid duplicates).
 */
export function generateOffsetNodes(base: NodePoint, count: number, offset: NodePoint): NodePoint[] {
  const c = Math.max(1, Math.floor(count));
  const ox = Number(offset.x) || 0;
  const oy = Number(offset.y) || 0;
  const oz = Number(offset.z) || 0;

  // If offset is zero in all axes, behave as single node to avoid duplicates
  if (ox === 0 && oy === 0 && oz === 0) return [ { x: base.x, y: base.y, z: base.z } ];

  const nodes: NodePoint[] = [];
  let current = { x: base.x, y: base.y, z: base.z };
  for (let i = 0; i < c; i++) {
    nodes.push({ x: current.x, y: current.y, z: current.z });
    current = { x: current.x + ox, y: current.y + oy, z: current.z + oz };
  }

  return nodes;
}
